import { useCallback } from "react";
import { useStore } from "jotai";
import type { GetContextMenuItemsParams, GetMainMenuItemsParams } from "ag-grid-community";
import { useMemoCallback } from "../../../utils/useMemoCallback";
import { useFormulaViewerModal } from "../modals/FormulaViewer";
import { useConfigureAlertsModal } from "../modals/Alerts";
import { useColumnSettingsModal } from "../modals/Formatting";
import { useConditionalFormattingModal } from "../modals/ConditionalFormatting";
import { useModal } from "../../components/Modal";
import { useInsertCurveDuplicate } from "../../menu-system/configs/sections/curve";
import { useUpsertQuickAccess } from "../../menu-system/configs/sections/add-to-favourites";
import { useClearFormatting } from "../../menu-system/configs/sections/format";
import { useRemoveColumnDialog } from "../../menu-system/configs/sections/remove-column";
import { useTraceTracePrecedentsModal } from "../../menu-system/configs/sections/trace-precedents";
import { useQuickAccess } from "../keyboard/useQuickAccess";
import {
  useActivePageId,
  usePageProducts,
  useUpdatePageProducts,
  useInsertPageProducts,
  useDeletePageProducts,
  useQueryConditionalFormattingRules,
  useUpsertConditionalFormattingRules,
  useConditionalFormattingCache,
  usePage,
  useUpdatePage,
  useAllProducts,
  useGlobalProducts,
} from "../../../data";
import { useFeatureFlags } from "../../../context/flags";
import { LIVE_CHART_FEED_SELECTION_FLAG } from "../../menu-system/live-chart-feeds";
import { useGridSettingsSelect } from "../../../data/grid-settings";
import {
  DEFAULT_CURVE_COLUMN_WIDTH,
  DEFAULT_INSERT_COLUMN_POSITION,
  useDefaultCurveColumnWidth,
  useInsertColumnPosition,
} from "../../../data/grid-settings/preferences";
import { useGrid } from "../stores";
import InsertCurveModal from "../modals/AddCurvesToPage";
import { ModalSheet } from "../../components/ModalSheet";
import { useGlobalisedPackageIds, useIsUmiInternalAdmin } from "../../curve-management/hooks";

// Legacy menu hooks
import { useGetMenuItemsGridSettings } from "./useGetMenuItemsGridSettings";
import { useGetMenuItemsCurveDetails } from "./useGetMenuItemsCurveDetails";
import { useGetMenuItemsHeaderSize } from "./useGetMenuItemsHeaderSize";
import { useGetMenuItemsFieldValue } from "./useGetMenuItemsFieldValue";
import { useGetMenuItemsColumnType } from "./useGetMenuItemsColumnType";
import { useGetMenuItemsColumnInsert } from "./useGetMenuItemsColumnInsert";
import { useGetMenuItemsShowFormula } from "./useGetMenuItemsShowFormula";
import { useGetMenuItemsRemoveColumn } from "./useGetMenuItemsRemoveColumn";
import { useGetMenuItemsEditCurve } from "./useGetMenuItemsEditCurve";
import { useGetMenuItemsFormatting } from "./useGetMenuItemsFormatting";
import { useGetMenuItemsConditionalFormatting } from "./useGetMenuItemsConditionalFormatting";
import { useGetMenuItemsAlerts } from "./useGetMenuItemsAlerts";
import { useGetMenuItemsQuickAccess } from "./useGetMenuItemsQuickAccess";
import { useGetMenuItemsClipboard } from "./useGetMenuItemsClipboard";
import { useGetMenuItemsCharts } from "./useGetMenuItemsCharts";
import { useGetMenuItemsExportData } from "./useGetMenuItemsExportData";
import { useGetMenuItemsColumn } from "./useGetMenuItemsColumn";
import { useGetMenuItemsChartRange } from "./useGetMenuItemsChartRange";
import { useGetMenuItemsTracePrecedents } from "./useGetMenuItemsTracePrecedents";

import { getMenuItemContext } from "./helpers";
import { useCurrentUser, useUserSubscriptionTier } from "../../../data/user";
import { useCurrentUserOrg, useUserId } from "../../../context/auth/hooks";

export const useGetProductsContextMenu = () => {
  const user = useCurrentUser();
  const { data: org } = useCurrentUserOrg();
  const orgNeedsHistoricalData = org?.data?.organisation_by_pk?.needs_historical_data ?? false;
  const { disabledFeatures } = useUserSubscriptionTier();
  const userId = useUserId();
  const pageId = useActivePageId();

  // Dependencies for ActionContext
  const store = useStore();
  const formulaModal = useFormulaViewerModal();
  const alertsModal = useConfigureAlertsModal();
  const columnSettingsModal = useColumnSettingsModal();
  const conditionalFormattingModal = useConditionalFormattingModal();
  const openRemoveColumnDialog = useRemoveColumnDialog();
  const tracePrecedentsModal = useTraceTracePrecedentsModal();
  const insertCurveDuplicate = useInsertCurveDuplicate();
  const upsertQuickAccess = useUpsertQuickAccess();
  const updatePageProducts = useUpdatePageProducts();
  const insertPageProducts = useInsertPageProducts();
  const deletePageProducts = useDeletePageProducts();
  const upsertConditionalFormattingRules = useUpsertConditionalFormattingRules(pageId);
  const clearFormatting = useClearFormatting();
  const conditionalFormattingRules = useQueryConditionalFormattingRules(pageId);
  const conditionalFormattingCache = useConditionalFormattingCache(pageId);
  const dontShowRemoveCurveWarning =
    useGridSettingsSelect((s) => s?.dont_show_remove_curve_confirmation_dialog).data ?? false;
  const insertColumnPosition = useInsertColumnPosition().data ?? DEFAULT_INSERT_COLUMN_POSITION;
  const defaultCurveColumnWidth = useDefaultCurveColumnWidth().data ?? DEFAULT_CURVE_COLUMN_WIDTH;
  const pageProducts = usePageProducts(pageId);
  const quickAccess = useQuickAccess();
  const [gridApi] = useGrid();
  const page = usePage(pageId);
  const updatePage = useUpdatePage();
  const [liveChartFeedSelectionFlag] = useFeatureFlags(LIVE_CHART_FEED_SELECTION_FLAG);
  const liveChartFeedSelectionEnabled = Boolean(liveChartFeedSelectionFlag);
  const productsById = useAllProducts().data;
  const globalProducts = useGlobalProducts().data;
  const isUmiInternalAdmin = useIsUmiInternalAdmin();
  const globalisedPackageIds = useGlobalisedPackageIds();

  const insertCurveModal = useModal(
    useCallback(
      (props: { curveWidth?: number }) => (
        <ModalSheet className="!w-[80vw] !max-w-[600px] p-[var(--padding-medium-500,15px)]">
          <InsertCurveModal
            curveWidth={props.curveWidth}
            onCancel={insertCurveModal.close}
            onDone={insertCurveModal.close}
          />
        </ModalSheet>
      ),
      [],
    ),
  );

  // Legacy menu hooks
  const getGridSettings = useGetMenuItemsGridSettings();
  const getCurveDetails = useGetMenuItemsCurveDetails();
  const getHeaderSize = useGetMenuItemsHeaderSize();
  const getField = useGetMenuItemsFieldValue();
  const getColumnType = useGetMenuItemsColumnType();
  const getInsertColumn = useGetMenuItemsColumnInsert();
  const getShowFormula = useGetMenuItemsShowFormula();
  const getRemoveCulumn = useGetMenuItemsRemoveColumn();
  const getEditCurve = useGetMenuItemsEditCurve();
  const getColumn = useGetMenuItemsColumn();
  const getFormat = useGetMenuItemsFormatting();
  const getConditionalFormatting = useGetMenuItemsConditionalFormatting();
  const getAlerts = useGetMenuItemsAlerts();
  const getQuickAccess = useGetMenuItemsQuickAccess();
  const getClipboard = useGetMenuItemsClipboard();
  const getCharts = useGetMenuItemsCharts();
  const getChartRange = useGetMenuItemsChartRange();
  const getExport = useGetMenuItemsExportData();
  const getTracePrecedence = useGetMenuItemsTracePrecedents();

  return useMemoCallback((params: GetContextMenuItemsParams | GetMainMenuItemsParams) => {
    const ctx = getMenuItemContext(params, {
      user,
      disabledFeatures,
      orgNeedsHistoricalData,
      userId,
      store,
      formulaModal,
      insertCurveModal,
      alertsModal,
      columnSettingsModal,
      conditionalFormattingModal,
      openRemoveColumnDialog,
      tracePrecedentsModal,
      insertCurveDuplicate,
      upsertQuickAccess,
      updatePageProducts,
      insertPageProducts,
      deletePageProducts,
      upsertConditionalFormattingRules,
      clearFormatting,
      conditionalFormattingRules: conditionalFormattingRules.data,
      conditionalFormattingCache,
      gridApi: gridApi ?? null,
      pageProductsData: pageProducts.data,
      quickAccessData: quickAccess.data,
      dontShowRemoveCurveWarning,
      insertColumnPosition,
      defaultCurveColumnWidth,
      pageId,
      page: page.data,
      updatePage,
      liveChartFeedSelectionEnabled,
      productsById,
      globalProducts,
      isUmiInternalAdmin,
      globalisedPackageIds,
    });

    const conditialAndAlerts = [...getConditionalFormatting(ctx), ...getAlerts(ctx)];
    return [
      ...getGridSettings(ctx),
      ...getCurveDetails(ctx),
      ...getHeaderSize(ctx),
      ...(ctx.isHeaderMenu && ctx.isProduct ? ["separator"] : []),
      ...getField(ctx),
      ...getColumnType(ctx),
      ...getInsertColumn(ctx),
      ...getShowFormula(ctx),
      ...getTracePrecedence(ctx),
      ...getEditCurve(ctx),
      ...getRemoveCulumn(ctx),
      ...getColumn(ctx),
      "separator",
      ...getFormat(ctx),
      "separator",
      ...conditialAndAlerts,
      ...(conditialAndAlerts.length > 0 ? ["separator"] : []),
      ...getQuickAccess(ctx),
      ...getClipboard(ctx),
      ...getCharts(ctx),
      ...getChartRange(ctx),
      ...getExport(ctx),
    ];
  });
};
